import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { safeStr } from "../components/reservation/reservationFlow.logic";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const VERIFY_PATH = "/prod/mpgs/verify-payment-rental"; // your API route path
const BASE =
  safeStr(process.env.REACT_APP_PAYMENTS_API_BASE);
async function postJson(path, body) {
  const resp = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
  const text = await resp.text();
  let data;
  try { data = text ? JSON.parse(text) : {}; } catch { data = { raw: text }; }
  return { ok: resp.ok, status: resp.status, data };
}

export default function PayReturnPage() {
  const q = useQuery();
  const iid = q.get("iid") || q.get("rid") || "";
  const nonce = q.get("nonce") || "";
  const resultIndicator = q.get("resultIndicator") || "";

  const [state, setState] = useState({ stage: "loading", msg: "" });
  const triesRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (state.stage !== "success" || !iid) return;
    try {
      window.sessionStorage.removeItem(`mpgs_${iid}`);
      window.sessionStorage.removeItem(`pay_redirect_${iid}`);
    } catch {
      // ignore
    }
  }, [iid, state.stage]);

  useEffect(() => {
    let cancelled = false;

    async function verifyOnce() {
      triesRef.current += 1;
      const r = await postJson(VERIFY_PATH, { inboundRequestId: iid, nonce, resultIndicator });
      if (!iid || !nonce || !resultIndicator) {
        setState({ stage: "error", msg: "Missing payment return parameters (iid/nonce/resultIndicator)." });
        return;
      }
      if (cancelled) return;

      if (!r.ok) {
        setState({
          stage: "error",
          msg: r.data?.message || `Verify failed (HTTP ${r.status})`,
          details: r.data,
        });
        return;
      }

      const d = r.data || {};

      if (d.paid) {
        setState({
          stage: "success",
          msg: "Payment confirmed.",
          details: d,
        });
        return;
      }

      if (d.failed) {
        setState({
          stage: "failed",
          msg: "Payment failed or was declined.",
          details: d,
        });
        return;
      }

      if (d.processing) {
        // Poll up to ~30s (15 tries x 2s)
        if (triesRef.current < 15) {
          setState({
            stage: "processing",
            msg: "Payment is processing. Verifying…",
            details: d,
          });
          timerRef.current = setTimeout(verifyOnce, 2000);
          return;
        }

        setState({
          stage: "processing_timeout",
          msg: "Payment is still processing. If it completed, you’ll receive an email confirmation shortly.",
          details: d,
        });
        return;
      }

      setState({
        stage: "unknown",
        msg: d.message || "Could not confirm payment.",
        details: d,
      });
    }

    if (!iid || !nonce) {
      setState({ stage: "error", msg: "Missing payment return parameters (iid/nonce)." });
      return;
    }

    verifyOnce();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [iid, nonce, resultIndicator]);

  const boxStyle = {
    maxWidth: 760,
    margin: "28px auto",
    padding: 18,
    borderRadius: 16,
    border: "1px solid #e5e7eb",
    background: "#fff",
  };

  const wrapStyle = {
    minHeight: "70vh",
    background: "#f3f4f6",
    padding: "24px 12px",
  };

  const details = state.details || {};
  const money = (a, c) => (a != null && c ? `${a} ${c}` : "");

  return (
    <div style={wrapStyle}>
      <div style={boxStyle}>
        <div style={{ fontWeight: 900, fontSize: 18, color: "#111827" }}>
          Payment return
        </div>

        <div style={{ marginTop: 8, color: "#6b7280" }}>
          {state.msg}
        </div>

        {state.stage === "success" && (
          <div style={{ marginTop: 14, padding: 12, borderRadius: 12, background: "#ecfdf5", border: "1px solid #a7f3d0" }}>
            <div style={{ fontWeight: 800, color: "#065f46" }}>Confirmed</div>
            <div style={{ marginTop: 6, color: "#065f46" }}>
              Paid: {money(details.capturedAmount || details.amountPaid, details.currency)}
            </div>
            <div style={{ marginTop: 10 }}>
              <Link to="/" style={{ color: "#111827", fontWeight: 800 }}>
                Return to home
              </Link>
            </div>
          </div>
        )}

        {(state.stage === "failed" || state.stage === "error") && (
          <div style={{ marginTop: 14, padding: 12, borderRadius: 12, background: "#fef2f2", border: "1px solid #fecaca" }}>
            <div style={{ fontWeight: 800, color: "#991b1b" }}>
              {state.stage === "failed" ? "Payment failed" : "Error"}
            </div>
            <div style={{ marginTop: 6, color: "#991b1b" }}>
              {state.msg}
            </div>
            <div style={{ marginTop: 10 }}>
              <Link to="/" style={{ color: "#111827", fontWeight: 800 }}>
                Return to home
              </Link>
            </div>
          </div>
        )}

        {(state.stage === "processing" || state.stage === "processing_timeout" || state.stage === "loading") && (
          <div style={{ marginTop: 14, padding: 12, borderRadius: 12, background: "#eff6ff", border: "1px solid #bfdbfe" }}>
            <div style={{ fontWeight: 800, color: "#1d4ed8" }}>
              {state.stage === "processing_timeout" ? "Processing" : "Verifying"}
            </div>
            <div style={{ marginTop: 6, color: "#1d4ed8" }}>
              Reference: {iid}
            </div>
          </div>
        )}

        <details style={{ marginTop: 14 }}>
          <summary style={{ cursor: "pointer", color: "#6b7280" }}>Technical details</summary>
          <pre style={{ marginTop: 10, background: "#111827", color: "#e5e7eb", padding: 12, borderRadius: 12, overflow: "auto" }}>
            {JSON.stringify({ iid, ...details }, null, 2)}
          </pre>
        </details>
      </div>
    </div>
  );
}
